// src/components/CustomChat.js
import React, { useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import "../styles/CustomChat.css";
import { sendMessage } from "../services/assistantService";

export default function CustomChat({
  step,
  setStep,
  onShouldOpenFuturisticDetails,
  futuristicDetailsStepIndex,
  setSwipableApartments // New prop to set apartment list for swiper
}) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [userId] = useState(uuidv4());
  const messagesEndRef = useRef(null);
  const hasSentInitial = useRef(false);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Send initial "Salam" message silently
    if (!hasSentInitial.current) {
      hasSentInitial.current = true;
      handleSend("Salam", true);
    }
  }, []); // Empty dependency array ensures this runs only once on mount

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSend = async (text = input, silent = false) => {
    const trimmedText = text.trim();
    if (!trimmedText) return;

    if (!silent) {
      const userMessage = { role: "user", text: trimmedText };
      setMessages((prev) => [...prev, userMessage]);
      setInput("");
    }

    try {
      const response = await sendMessage(userId, trimmedText);
      const {
        message,
        suggestions,
        phase,
        metadata,
      } = response.response;

      // Handle apartment list for swiper
      if (metadata && metadata.apartmentListForSwiper) {
        setSwipableApartments(metadata.apartmentListForSwiper);
      } else {
        // If no new list is explicitly sent, decide whether to clear the existing one.
        // For now, we only update/clear when the bot sends relevant data.
        // To clear if no list is present in a response: setSwipableApartments([]);
      }

      // Handle direct opening of FuturisticDetails
      // This could be after a selection from the swiper (handled in AssistantScreen)
      // OR if the bot directly offers one detailed view.
      if (metadata && metadata.apartmentForDetailedView) {
        // This case is for when the bot *directly* provides data for the details panel,
        // bypassing the swiper, or if it's a response to a specific query that leads here.
        onShouldOpenFuturisticDetails(metadata.apartmentForDetailedView);
        if (step !== futuristicDetailsStepIndex) {
          setStep(futuristicDetailsStepIndex);
        }
        setSwipableApartments([]); // Clear swiper when showing specific details panel
      } else if (typeof phase === "number" && phase !== step) {
        // Only update step if not handling a direct detail view,
        // as that action might also set the step.
        setStep(phase);
      }

      const botMessage = {
        role: "bot",
        text: message,
        suggestions: suggestions || [],
        metadata: metadata || {},
      };
      setMessages((prev) => [...prev, botMessage]);

    } catch (error) {
      console.error("Chat send error:", error);
      setMessages((prev) => [
        ...prev,
        { role: "bot", text: "Une erreur est survenue lors de la communication." },
      ]);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSuggestionClick = (suggestionText) => {
    handleSend(suggestionText);
  };

  return (
    <div className="chat-container flex-grow">
      <div className="chat-window">
        <div className="chat-messages">
          {messages.map((msg, i) => (
            <div key={i} className={`chat-bubble ${msg.role}`}>
              <div>{typeof msg.text === 'object' ? JSON.stringify(msg.text) : msg.text}</div>

              {/* Corrected metadata display block */}
              {msg.metadata && typeof msg.metadata === 'object' && msg.metadata.monthly_payment && (
                <div className="bank-summary">
                  💸 <strong>Mensualité:</strong> {Number(msg.metadata.monthly_payment).toLocaleString("fr-FR")} MAD<br />
                  {msg.metadata.max_lendable_amount && (
                    <>🏦 <strong>Prêt max:</strong> {Number(msg.metadata.max_lendable_amount).toLocaleString("fr-FR")} MAD<br /></>
                  )}
                  {msg.metadata.applied_rate && (
                    <>📊 <strong>Taux appliqué:</strong> {msg.metadata.applied_rate}%</>
                  )}
                </div>
              )}

              {/* Suggestions display */}
              {msg.suggestions?.length > 0 && msg.role === 'bot' && (
                <div className="suggestions-outer">
                  {msg.suggestions.map((suggestion, j) => (
                    <button
                      key={j}
                      className="suggestion-button"
                      onClick={() => handleSuggestionClick(suggestion)}
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        <div className="chat-input-area">
          <input
            className="chat-input"
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Tapez votre message..."
          />
          <button className="chat-send-button" onClick={() => handleSend()}>
            ➤
          </button>
        </div>
      </div>
    </div>
  );
}
